import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Upload, Trash2, FileText, Download, QrCode, Loader2, ImageIcon, Activity, RadioTower, Split, FileDown, Phone, PenLine, MapPin, Bell, AlertTriangle } from "lucide-react";
import DeviceHistory from "@/components/DeviceHistory";
import ClientPushDialog from "@/components/ClientPushDialog";
import ClientCPE from "@/components/ClientCPE";
import { toast } from "sonner";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import { KeyRound } from "lucide-react";
import SpeedHistory from "@/components/SpeedHistory";
import { MODULES } from "@/lib/modules";
import { cableOptical } from "@/lib/cableOptical";
import { useI18n } from "@/context/I18nContext";
import { useSettings } from "@/context/SettingsContext";

export default function ObjectDetailPanel({ moduleKey, item, slacks = [], onClose, role, onTrace, onRefresh, onEdit, onShowRoute, onEditRoute }) {
  const cfg = MODULES[moduleKey];
  const { t } = useI18n();
  const { settings } = useSettings();
  const sipOn = !!settings?.sip?.enabled;
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [dist, setDist] = useState(null);
  const fileRef = useRef();
  const qrRef = useRef();
  const canEdit = role !== "odczyt";
  const token = dlToken();
  const [pin, setPin] = useState(item.access_pin || "");
  const [showPush, setShowPush] = useState(false);
  const [pushPrefill, setPushPrefill] = useState({ title: "", body: "" });
  const expiryAlert = useMemo(() => {
    const isCl = moduleKey === "clients";
    const isDev = ["onu", "olt", "switches"].includes(moduleKey);
    const dateStr = isCl ? item.service_end : (isDev ? item.warranty_until : null);
    if (!dateStr) return null;
    const end = new Date(dateStr);
    if (isNaN(end.getTime())) return null;
    const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
    if (days > 60) return null;
    return { days, date: String(dateStr).slice(0, 10), expired: days < 0, kind: isCl ? "contract" : "warranty" };
  }, [moduleKey, item.service_end, item.warranty_until]);
  const dlDoc = async (kind) => {
    try {
      const r = await api.get(`/clients/${item.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (e.response?.status === 409) toast.error(t("Klient ma wgrany własny plik — pobierz z portalu klienta"));
      else toast.error(t("Nie udało się wygenerować PDF"));
    }
  };
  const genPin = async () => {
    try { const { data } = await api.post(`/clients/${item.id}/generate-pin`); setPin(data.access_pin); toast.success(`${t("Nowy PIN klienta")}: ${data.access_pin}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [objColor, setObjColor] = useState(item.color || "");
  const saveObjColor = async (c) => {
    setObjColor(c || "");
    try { await api.put(`/${moduleKey}/${item.id}`, { color: c }); item.color = c; onRefresh && onRefresh(); toast.success(c ? t("Zapisano kolor obiektu") : t("Przywrócono domyślny kolor")); }
    catch { toast.error(t("Błąd zapisu")); }
  };
  const isClient = moduleKey === "clients";
  const isCatalog = !!cfg.catalog;
  const isRoute = moduleKey === "routes";
  const [routeDoc, setRouteDoc] = useState(null);

  const deepLink = `${window.location.origin}/?open=${moduleKey}&id=${item.id}`;

  const load = useCallback(async () => {
    const { data } = await api.get(`/attachments?entity_type=${moduleKey}&entity_id=${item.id}`);
    setFiles(data);
  }, [moduleKey, item.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isClient) api.get(`/trace/${item.id}`).then((r) => setDist(r.data)).catch(() => setDist(null));
    if (isRoute) api.get(`/routes/${item.id}/document`).then((r) => setRouteDoc(r.data)).catch(() => setRouteDoc(null));
  }, [isClient, isRoute, item.id]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("entity_type", moduleKey);
    fd.append("entity_id", item.id);
    fd.append("file", file);
    try {
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Plik przesłany"));
      load();
      if (isCatalog && onRefresh) onRefresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm(t("Usunąć plik?"))) return;
    await api.delete(`/files/${id}`);
    toast.success(t("Usunięto plik"));
    load();
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `QR_${cfg.singular}_${item[cfg.display] || item.id}.png`;
    a.click();
  };

  const photos = files.filter((f) => f.kind === "photo");
  const docs = files.filter((f) => f.kind !== "photo");
  const fileUrl = (id) => `${API}/files/${id}?auth=${token}`;

  const delSlack = async (id) => {
    try {
      const { data } = await api.delete(`/cable-slack/${id}`);
      const opt = data.optical;
      toast.success(opt ? `${t("Zapas usunięty")} · ${t("Optyczna")}: ${opt.optical_length_m} m` : t("Zapas usunięty"));
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const dlCablePassport = async () => {
    try {
      const r = await api.get(`/cables/${item.id}/passport`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `paszport_kabla_${item.number || item.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Błąd generowania paszportu kabla"));
    }
  };

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="object-detail-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-2 min-w-0">
          <cfg.icon size={18} className="text-cyan-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{cfg.singular}</div>
            <h2 className="font-heading font-bold text-base truncate">{item[cfg.display] || item.name}</h2>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {role !== "odczyt" && onEdit && (
            <button onClick={() => onEdit(item)} data-testid="detail-edit" className="text-cyan-300 hover:text-cyan-100 p-1" title={t("Edytuj")}><PenLine size={17} /></button>
          )}
          <button onClick={onClose} data-testid="detail-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Fields */}
        <div className="p-4 border-b border-slate-800">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {cfg.fields.filter((f) => item[f.name] != null && item[f.name] !== "").slice(0, 10).map((f) => (
              <div key={f.name} className="min-w-0">
                <div className="text-[10px] text-slate-500 uppercase">{t(f.label)}</div>
                <div className="text-sm text-slate-200 truncate">{String(item[f.name])}</div>
              </div>
            ))}
            {item.lat != null && (
              <div className="col-span-2">
                <div className="text-[10px] text-slate-500 uppercase">GPS</div>
                <div className="text-sm font-mono text-cyan-400">{item.lat?.toFixed?.(4)}, {item.lng?.toFixed?.(4)}</div>
              </div>
            )}
          </div>
        </div>

        {canEdit && MODULES[moduleKey]?.geo === "point" && !cfg.catalog && (
          <div className="p-4 border-b border-slate-800 flex items-center gap-3" data-testid="detail-color-row">
            <span className="text-xs text-slate-400 flex-1">{t("Kolor tego obiektu na mapie")}</span>
            <input type="color" value={objColor || cfg.marker || "#38bdf8"} onChange={(e) => saveObjColor(e.target.value)}
              data-testid="detail-color-input" className="w-9 h-8 rounded bg-transparent border border-slate-600 cursor-pointer" />
            {objColor && (
              <button onClick={() => saveObjColor(null)} data-testid="detail-color-reset"
                className="text-xs text-slate-400 hover:text-red-400">{t("Domyślny")}</button>
            )}
          </div>
        )}

        {canEdit && MODULES[moduleKey]?.geo === "line" && item.geometry?.length > 1 && onEditRoute && (
          <div className="p-4 border-b border-slate-800">
            <button onClick={() => onEditRoute(item)} data-testid="detail-edit-route"
              className="w-full bg-amber-600 hover:bg-amber-500 rounded py-2 text-sm flex items-center justify-center gap-2 text-white">
              <PenLine size={15} /> {t("Edytuj trasę na mapie")}
            </button>
          </div>
        )}

        {expiryAlert && (
          <div data-testid="expiry-alert"
            className={`mx-4 mt-4 rounded-md border px-3 py-2 text-sm flex items-center gap-2 ${expiryAlert.expired ? "border-red-500 bg-red-500/10 text-red-300" : "border-amber-500 bg-amber-500/10 text-amber-300"}`}>
            <AlertTriangle size={16} className="shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{expiryAlert.kind === "contract" ? t("Umowa") : t("Gwarancja sprzętu")}: {expiryAlert.expired ? t("wygasła") : `${t("wygasa za")} ${expiryAlert.days} ${t("dni")}`}</div>
              <div className="text-xs opacity-80">{t("Data")}: {expiryAlert.date}</div>
            </div>
            {isClient && canEdit && (
              <button data-testid="expiry-notify-btn"
                onClick={() => { setPushPrefill({ title: t("Przypomnienie o umowie"), body: `${t("Informujemy, że Państwa umowa")} ${expiryAlert.expired ? t("wygasła") : `${t("wygasa")} ${expiryAlert.date}`}. ${t("Prosimy o kontakt w sprawie przedłużenia.")}` }); setShowPush(true); }}
                className="bg-indigo-700 hover:bg-indigo-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1 shrink-0">
                <Bell size={13} /> {t("Powiadom")}
              </button>
            )}
          </div>
        )}

        {["onu", "olt", "switches", "clients"].includes(moduleKey) && (
          <DeviceHistory deviceType={moduleKey} deviceId={item.id} role={role} />
        )}

        {isClient && <ClientCPE clientId={item.id} role={role} />}

        {moduleKey === "cables" && (() => { const o = cableOptical(item, slacks); const mine = (slacks || []).filter((s) => s.cable === item.id); return (
          <div className="p-4 border-b border-slate-800" data-testid="cable-optical-detail">
            <div className="text-[10px] text-slate-500 uppercase mb-2">{t("Długości")}</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-lg font-bold text-slate-200">{o.route}</div><div className="text-[10px] text-slate-500">{t("Trasa")} [m]</div></div>
              <div><div className="text-lg font-bold text-rose-300">{o.slack}</div><div className="text-[10px] text-slate-500">{t("Zapasy")} [m]</div></div>
              <div><div className="text-lg font-bold text-cyan-300">{o.optical}</div><div className="text-[10px] text-slate-500">{t("Optyczna")} [m]</div></div>
            </div>
            <div className="mt-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1.5">{t("Zapasy kabla")} ({mine.length})</div>
              {mine.length === 0 ? (
                <div className="text-xs text-slate-500">{t("Brak zapasów — dodaj klikając słup/studnię/mufę na mapie")}</div>
              ) : (
                <div className="space-y-1.5" data-testid="cable-slack-list">
                  {mine.map((s) => (
                    <div key={s.id} data-testid={`cable-slack-item-${s.id}`}
                      className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-800/40 px-2.5 py-1.5">
                      <span className="w-2 h-2 rounded-full bg-rose-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-200"><b>{s.length_m} m</b>{s.reserve_type ? ` · ${s.reserve_type}` : ""}</div>
                        {s.note ? <div className="text-[11px] text-slate-500 truncate">{s.note}</div> : null}
                      </div>
                      {role !== "odczyt" && (
                        <button onClick={() => delSlack(s.id)} data-testid={`cable-slack-del-${s.id}`}
                          title={t("Usuń zapas")} className="p-1 rounded hover:bg-red-600/30 text-red-400 shrink-0"><Trash2 size={13} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button onClick={dlCablePassport} data-testid="cable-passport-btn"
                className="w-full mt-2 bg-cyan-700 hover:bg-cyan-600 rounded py-1.5 text-xs flex items-center justify-center gap-1.5">
                <FileDown size={13} /> {t("Paszport kabla (PDF)")}
              </button>
              {item.geometry?.length > 1 && (
                <button onClick={() => onShowRoute && onShowRoute(item)} data-testid="cable-show-route-btn"
                  className="w-full mt-2 bg-red-700 hover:bg-red-600 rounded py-1.5 text-xs flex items-center justify-center gap-1.5">
                  <MapPin size={13} /> {t("Pokaż trasę na mapie (miga)")}
                </button>
              )}
            </div>
          </div>
        ); })()}

        {/* Client: trace + distances */}
        {isClient && (
          <div className="p-4 border-b border-slate-800" data-testid="client-trace-block">
            {sipOn && item.phone && (
              <button onClick={() => window.dispatchEvent(new CustomEvent("fp-sip-call", { detail: { number: item.phone } }))}
                data-testid="client-sip-call"
                className="w-full bg-green-700 hover:bg-green-600 rounded py-2 text-sm flex items-center justify-center gap-2 mb-2">
                <Phone size={15} /> {t("Zadzwoń")}: {item.phone}
              </button>
            )}
            <button onClick={() => onTrace && onTrace(item)} data-testid="client-trace-btn"
              className="w-full bg-cyan-700 hover:bg-cyan-600 rounded py-2 text-sm flex items-center justify-center gap-2 mb-3">
              <Activity size={15} /> {t("Pokaż trasę sygnału")}
            </button>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button onClick={() => dlDoc("contract")} data-testid="client-contract-btn" className="bg-slate-700 hover:bg-slate-600 rounded py-2 text-xs flex items-center justify-center gap-1"><FileDown size={13} /> {t("Umowa")}</button>
              <button onClick={() => dlDoc("rodo")} data-testid="client-rodo-btn" className="bg-slate-700 hover:bg-slate-600 rounded py-2 text-xs flex items-center justify-center gap-1"><FileDown size={13} /> RODO</button>
              <button onClick={genPin} data-testid="client-genpin-btn" className="bg-slate-700 hover:bg-slate-600 rounded py-2 text-xs flex items-center justify-center gap-1"><KeyRound size={13} /> PIN</button>
            </div>
            {pin && <div className="text-xs text-slate-400 mb-3" data-testid="client-pin">{t("PIN do app klienta")}: <b className="font-mono text-cyan-300">{pin}</b></div>}
            {canEdit && (
              <button onClick={() => { setPushPrefill({ title: "", body: "" }); setShowPush(true); }} data-testid="client-push-btn"
                className="w-full bg-indigo-700 hover:bg-indigo-600 rounded py-2 text-sm flex items-center justify-center gap-2 mb-3">
                <Bell size={15} /> {t("Wyślij powiadomienie push")}
              </button>
            )}
            <ClientPushDialog open={showPush} onClose={() => setShowPush(false)} clientId={item.id} clientName={item.name || item.address || ""} defaultTitle={pushPrefill.title} defaultBody={pushPrefill.body} />
            <SpeedHistory endpoint={`/clients/${item.id}/speedtests`} />
            {dist && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-300"><RadioTower size={14} className="text-blue-400" /> {t("Odległość do OLT")}</span>
                  <span className="font-mono text-cyan-400">{dist.dist_to_olt_m != null ? `${(dist.dist_to_olt_m / 1000).toFixed(3)} km` : "—"}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-300"><Split size={14} className="text-orange-400" /> {t("Do splittera")} {dist.nearest_splitter_name || ""}</span>
                  <span className="font-mono text-cyan-400">{dist.dist_to_splitter_m != null ? `${(dist.dist_to_splitter_m / 1000).toFixed(3)} km` : "—"}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{t("Tłumienie toru")}</span>
                  <span className="font-mono text-cyan-400">{Number(dist.attenuation_db).toFixed(2)} dB</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Catalog: datasheet PDF */}
        {isCatalog && (
          <div className="p-4 border-b border-slate-800">
            <a href={`${API}/catalog/${moduleKey}/${item.id}/card?auth=${token}`} target="_blank" rel="noreferrer"
              data-testid="catalog-card-pdf"
              className="w-full bg-red-700 hover:bg-red-600 rounded py-2 text-sm flex items-center justify-center gap-2 text-white">
              <FileDown size={15} /> {t("Karta katalogowa (PDF)")}
            </a>
          </div>
        )}

        {/* Route document */}
        {isRoute && routeDoc && (
          <div className="p-4 border-b border-slate-800" data-testid="route-document-block">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Dokument trasy</span>
              <a href={`${API}/routes/${item.id}/document.pdf?auth=${token}`} target="_blank" rel="noreferrer"
                data-testid="route-document-pdf"
                className="text-xs bg-red-700 hover:bg-red-600 rounded px-2.5 py-1 flex items-center gap-1.5 text-white">
                <FileDown size={13} /> PDF
              </a>
            </div>
            <div className="text-xs text-slate-400 mb-2">{t("Słupów na trasie:")} <b className="text-slate-200">{routeDoc.pole_count}</b> · {t("Kabli:")} <b className="text-slate-200">{routeDoc.cables.length}</b></div>
            {routeDoc.poles.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Słupy</div>
                <div className="space-y-1">
                  {routeDoc.poles.map((p, i) => (
                    <div key={i} className="text-xs bg-slate-800 rounded px-2 py-1.5 flex items-center justify-between">
                      <span className="text-slate-200">{p.number} <span className="text-slate-500">· {p.owner}</span></span>
                      <span className="text-slate-400 font-mono">{p.cables.join(", ") || "brak kabli"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {routeDoc.cables.map((c, i) => (
              <div key={i} className="mb-2 bg-slate-800 rounded p-2">
                <div className="text-xs font-medium text-slate-200">Kabel {c.number} <span className="text-green-400">{c.free.length} wolnych</span> / <span className="text-red-400">{c.occupied.length} zajętych</span></div>
                {c.occupied.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.occupied.map((f) => (
                      <span key={f.number} className="text-[10px] bg-red-900/30 border border-red-700/40 rounded px-1.5 py-0.5" title={f.leads_to}>
                        #{f.number} {f.color}{f.leads_to ? ` → ${f.leads_to}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="p-4 border-b border-slate-800">
          <button onClick={() => setShowQR((v) => !v)} data-testid="qr-toggle"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-400">
            <QrCode size={16} /> {t("Kod QR obiektu")} {showQR ? "▲" : "▼"}
          </button>
          {showQR && (
            <div className="mt-3 flex flex-col items-center gap-2" ref={qrRef}>
              <div className="bg-white p-3 rounded-md">
                <QRCodeCanvas value={deepLink} size={150} level="M" includeMargin={false} />
              </div>
              <p className="text-[11px] text-slate-500 text-center">{t("Zeskanuj, aby otworzyć dokumentację obiektu")}</p>
              <button onClick={downloadQR} data-testid="qr-download"
                className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded px-3 py-1.5 flex items-center gap-1.5">
                <Download size={13} /> {t("Pobierz kod QR")}
              </button>
            </div>
          )}
        </div>

        {/* Photos */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-1.5 mb-2 text-sm text-slate-300"><ImageIcon size={15} /> {t("Zdjęcia")} ({photos.length})</div>
          {photos.length === 0 && <p className="text-xs text-slate-500">{t("Brak zdjęć")}</p>}
          <div className="grid grid-cols-3 gap-2">
            {photos.map((f) => (
              <div key={f.id} className="relative group aspect-square rounded overflow-hidden border border-slate-700">
                <img src={fileUrl(f.id)} alt={f.original_filename} className="w-full h-full object-cover" />
                {canEdit && (
                  <button onClick={() => del(f.id)} className="absolute top-1 right-1 bg-red-600/90 rounded p-1 opacity-0 group-hover:opacity-100">
                    <Trash2 size={11} className="text-white" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div className="p-4">
          <div className="flex items-center gap-1.5 mb-2 text-sm text-slate-300"><FileText size={15} /> {t("Dokumenty")} ({docs.length})</div>
          {docs.length === 0 && <p className="text-xs text-slate-500">{t("Brak dokumentów")}</p>}
          <div className="space-y-1.5">
            {docs.map((f) => (
              <div key={f.id} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-2.5 py-1.5">
                <FileText size={15} className="text-slate-400 shrink-0" />
                <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-sm text-cyan-400 hover:underline truncate flex-1">{f.original_filename}</a>
                {canEdit && <button onClick={() => del(f.id)} className="text-red-400 hover:text-red-300"><Trash2 size={13} /></button>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="p-3 border-t border-slate-700">
          <label className="w-full bg-blue-600 hover:bg-blue-500 rounded py-2 text-sm flex items-center justify-center gap-2 cursor-pointer">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {t("Prześlij zdjęcie / dokument")}
            <input ref={fileRef} type="file" accept="image/*,.pdf,.txt,.csv" className="hidden" onChange={onUpload} data-testid="upload-input" />
          </label>
        </div>
      )}
    </div>
  );
}
